import { Component, OnInit } from '@angular/core'
import { Router, ActivatedRoute } from '@angular/router'
import { FormGroup, FormBuilder, Validators } from '@angular/forms'
import { NuclearObservable } from '../../nuclear-observable'
import { BaseComponent } from '../../components/base.component'
import { Account, User, Customer } from '../../types'
import { AccountActions } from '../../actions/account-actions'
import { customerGetters } from '../../stores/customer-store'
import { hasSetupAccountProfile } from '../../utils'
import { clientsServedOptions, referenceOptions, estimatedMonthlyBudgetOptions } from '../../content/signup-content'
import { CustomerActions } from 'src/actions/customer-actions'
import { UserActions } from 'src/actions/user-actions'
import { userGetters } from 'src/stores/user-store'

@Component({
  selector: 'account-signup-home-page',
  templateUrl: './account-signup-home-page.html'
})
export class AccountSignupHomePage extends BaseComponent implements OnInit {
  account: Account;
  accountName = '';
  form: FormGroup;
  submitted: boolean = false;
  private user: User;
  private formBuilder: FormBuilder;
  private saving: boolean = false;
  private accountActions: AccountActions;
  private router: Router;
  private customers: Customer[];
  private customer: Customer;
  clientsServedOptions = clientsServedOptions;
  referenceOptions = referenceOptions;
  estimatedMonthlyBudgetOptions = estimatedMonthlyBudgetOptions;

  constructor(
    formBuilder: FormBuilder,
    accountActions: AccountActions,
    router: Router,
    private customerActions: CustomerActions,
    private userActions: UserActions,
    private route: ActivatedRoute) {
    super()

    this.formBuilder = formBuilder
    this.accountActions = accountActions
    this.router = router
  }

  ngOnInit(): void {
    const accountObs = NuclearObservable.from<Account>(customerGetters.account, { toJS: true })
      .subscribe(account => {
        this.account = account
        this.accountName = this.account.accountName || 'your agency'
      })
    const customerObs = NuclearObservable.from<Customer[]>(customerGetters.shallowCustomers, { toJS: true }).subscribe(cs => this.customers = cs)

    this.userActions.loadUser().then(() => {
      const user = NuclearObservable.currentValue<User>(userGetters.user, { toJS: true })
      this.form.get('accountProfile').get('contactEmail').setValue(user.email)
    })

    this.customerActions.loadCustomers().then(() => {
      const customers = NuclearObservable.currentValue<Customer[]>(customerGetters.customers, { toJS: true })
      if (customers && customers.length > 0) {
        this.customer = customers[0]
        this.observeCustomer(this.customer.id, c => this.customer = c)
        this.accountName = this.customer?.businessName || 'your business'
      }
    })

    this.observers.push(accountObs, customerObs)
    this._initializeForm()
  }

  isWarningHidden(input: any): boolean {
    return input.valid || (input.pristine && !this.submitted) || (input.untouched && !this.submitted)
  }

  onSubmit(form: FormGroup): void {
    let allowRouting = !this.hasSetupAccountProfile() || (!!this.account.agency && this.customers.length === 0)
    this.submitted = true
    if (!this.saving && form.valid) {
      this.saving = true
      this.accountActions.updateAccount(form.value).then(() => {
        if (allowRouting) {
          if(this.account.agency) {
            this.router.navigate(['clients', 'home'], { relativeTo: this.route })
          } else {
            this.router.navigate(['signup', this.customer.id, 'about', 'industry'])
          }
        }
        this.saving = false
      })
      .catch(() => {
        this.saving = false
      })
    }
  }

  agencyActivationRoute(): Array<string|Object> {
    //TODO: Change to agency activation route (has to be a valid route to compile)
    return ['AgencyActivation']
  }

  hasSetupAccountProfile(): boolean {
    return hasSetupAccountProfile(this.account)
  }

  private _initializeForm():void {
    const profile = this.account?.accountProfile

    this.form = this.formBuilder.group({
      accountProfile: this.formBuilder.group({
        clientsServed: [profile?.clientsServed ?? null],
        contactName: [profile?.contactName ?? null, Validators.required],
        contactEmail: [profile?.contactEmail ?? (this.user?.email ?? null), [Validators.required, Validators.email]],
        contactTitle: [profile?.contactTitle ?? null, Validators.required],
        contactPhone: [profile?.contactPhone ?? null],
        referenceDetails: [profile?.referenceDetails ?? null, Validators.required],
        estimatedMonthlyBudget: [profile?.estimatedMonthlyBudget ?? null],
      })
    })
  }
}
